import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useCart } from "../context/CartContext";
import { createOrder } from "../lib/api";
import { money } from "../lib/ui";

const Field = ({ label, name, value, onChange, type = "text", required = true, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">{label}</span>
    <input
      data-testid={`checkout-${name}`}
      name={name}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      className="mt-2 w-full border border-line bg-sand px-4 py-3 font-body text-ink outline-none transition-colors focus:border-ink"
    />
  </label>
);

export default function Checkout() {
  const { items, subtotal, shipping, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ack, setAck] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", institution: "", address: "", city: "", state: "", zip_code: "", country: "USA", notes: "",
  });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!ack) {
      toast.error("Please acknowledge the Research Use Only terms.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        ruo_acknowledged: ack,
        items: items.map((i) => ({ slug: i.slug, name: i.name, price: i.price, size: i.size, quantity: i.quantity })),
      };
      const order = await createOrder(payload);
      clearCart();
      navigate(`/order/${order.order_number}`);
    } catch (err) {
      toast.error("Order failed", { description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-sand">
        <p className="font-display text-5xl tracking-tight text-ink">Nothing to check out.</p>
        <Link to="/#catalog" className="font-mono text-xs uppercase tracking-[0.2em] text-rust">← Browse the catalog</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="min-h-screen bg-sand pt-36">
      <div className="mx-auto max-w-[1500px] px-6 pb-32 md:px-12">
        <Link to="/" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/60 hover:text-ink">
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" strokeWidth={1.5} /> Continue Shopping
        </Link>
        <h1 className="mt-8 font-display text-6xl leading-none tracking-tighter text-ink md:text-8xl">Checkout</h1>

        <div className="mt-14 grid gap-16 lg:grid-cols-[1.4fr_1fr]">
          {/* Form */}
          <form onSubmit={submit} className="order-2 lg:order-1">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-rust">[ Shipping Details ]</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <Field label="Full Name" name="full_name" value={form.full_name} onChange={onChange} className="sm:col-span-2" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} />
              <Field label="Institution (optional)" name="institution" value={form.institution} onChange={onChange} required={false} />
              <Field label="Street Address" name="address" value={form.address} onChange={onChange} className="sm:col-span-2" />
              <Field label="City" name="city" value={form.city} onChange={onChange} />
              <Field label="State / Province" name="state" value={form.state} onChange={onChange} />
              <Field label="ZIP / Postal Code" name="zip_code" value={form.zip_code} onChange={onChange} />
              <Field label="Country" name="country" value={form.country} onChange={onChange} />
              <label className="block sm:col-span-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Order Notes (optional)</span>
                <textarea
                  data-testid="checkout-notes"
                  name="notes"
                  value={form.notes}
                  onChange={onChange}
                  rows={3}
                  className="mt-2 w-full border border-line bg-sand px-4 py-3 font-body text-ink outline-none transition-colors focus:border-ink"
                />
              </label>
            </div>

            <label className="mt-8 flex cursor-pointer items-start gap-3 border border-ruo/50 bg-ruo/5 p-5">
              <input
                data-testid="ruo-checkbox"
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-ruo"
              />
              <span className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.1em] text-ruo">
                I certify I am a qualified researcher and these products are for Research Use Only — not for
                human or veterinary consumption.
              </span>
            </label>

            <button
              data-testid="place-order-button"
              type="submit"
              disabled={loading}
              className="mt-8 w-full border border-ink bg-ink py-5 font-mono text-sm uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rust hover:border-rust disabled:opacity-50"
            >
              {loading ? "Placing Order…" : `Place Order — ${money(total)}`}
            </button>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-ink/40">
              Demo checkout · no payment is processed
            </p>
          </form>

          {/* Summary */}
          <div className="order-1 h-fit border border-line bg-surface p-6 md:p-8 lg:order-2 lg:sticky lg:top-36">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-rust">[ Order Summary ]</p>
            <div className="mt-6 space-y-5">
              {items.map((i) => (
                <div key={i.slug} className="flex gap-4">
                  <div className="h-16 w-16 shrink-0 overflow-hidden border border-line bg-sand">
                    <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex flex-1 justify-between">
                    <div>
                      <p className="font-display text-lg leading-none text-ink">{i.name}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/45">{i.size} · ×{i.quantity}</p>
                    </div>
                    <span className="font-mono text-sm text-ink">{money(i.price * i.quantity)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-2 border-t border-line pt-5 font-mono text-xs uppercase tracking-[0.15em] text-ink/70">
              <div className="flex justify-between"><span>Subtotal</span><span>{money(subtotal)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : money(shipping)}</span></div>
            </div>
            <div className="mt-4 flex justify-between border-t border-line pt-4">
              <span className="font-display text-2xl text-ink">Total</span>
              <span className="font-mono text-xl text-ink">{money(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
